import React from 'react';
import {
  TextInput,
  NumberInput,
  RadioButtonGroupInput,
  BooleanInput,
  required,
  useTranslate,
  usePermissions,
} from 'react-admin';
import { RichTextInput, RichTextInputToolbar } from 'ra-input-rich-text';
import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import moment from 'moment';
import { TICKET_GROUP_STATUSES } from '../../constants/events';
import { MaskedDateTimeInput } from '../form/event-utils/MaskedDateTimeInput';
import { TicketIdInput } from '../form/TicketIdInput';
import { useSourceLanguage } from '../../hooks/useSourceLanguage';

/** Fiyat: yalnızca admin yazabilir. Diğer roller readOnly (disabled değil — kayıtta fiyat silinmesin). */
const useAdminPriceFieldProps = (translate, isEditForm, scopedFormData) => {
  const { permissions } = usePermissions();
  const isAdmin = permissions?.crole === 'admin';
  const soldHint =
    isEditForm && scopedFormData?.sold > 0
      ? translate(
          'form.price_edit_sold_hint',
          'Bu ürünün satışı var. Fiyat değişikliği yeni satışlara yansır; eski biletlerin tutarı değişmez.',
        )
      : '';
  const adminOnlyHint = translate(
    'form.price_admin_only_hint',
    'Fiyatı yalnızca admin değiştirebilir.',
  );

  return {
    isAdmin,
    InputProps: isAdmin ? undefined : { readOnly: true },
    helperText: isAdmin ? soldHint : adminOnlyHint,
  };
};

/** Anahtar solda; açıkken bağlı alan(lar) aynı satırda sağında. */
const ToggleRow = ({ getSource, toggleSource, label, children }) => {
  const enabled = useWatch({ name: getSource(toggleSource) });
  return (
    <Box
      display="flex"
      flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
      alignItems="flex-start"
      gap={1.5}
      mb={0.5}
    >
      <Box flexShrink={0} minWidth={{ sm: 200 }} pt="9px">
        <BooleanInput
          source={getSource(toggleSource)}
          label={label}
          defaultValue={false}
          helperText={false}
          sx={{ m: 0 }}
        />
      </Box>
      {enabled ? (
        <Box display="flex" alignItems="flex-start" gap={1} flex={1} minWidth={0}>
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

const ProductRefundFields = ({ getSource, refundAction }) => {
  const translate = useTranslate();
  const startdate = useWatch({ name: 'startdate' });

  const validateDeadline = React.useCallback(
    (value) => {
      if (!value || !startdate) return undefined;
      const deadline = moment(value);
      const start = moment(startdate);
      if (deadline.isValid() && start.isValid() && deadline.isAfter(start)) {
        return translate(
          'form.refund_deadline_error',
          'İade son tarihi etkinlik başlangıcından sonra olamaz.',
        );
      }
      return undefined;
    },
    [startdate, translate],
  );

  return (
    <ToggleRow
      getSource={getSource}
      toggleSource="refundable"
      label={translate('form.refundable', 'İade edilebilir?')}
    >
      <Box flex={1} minWidth={0}>
        <MaskedDateTimeInput
          source={getSource('refundDeadlineAt')}
          label={translate('form.product_refund_deadline', 'İade son tarihi')}
          helperText={translate(
            'form.product_refund_deadline_hint',
            'Boş bırakılırsa etkinlik başlayana kadar iade yapılabilir.',
          )}
          validate={validateDeadline}
        />
      </Box>
      {refundAction}
    </ToggleRow>
  );
};

export const StandardProductForm = ({
  getSource,
  showSold,
  scopedFormData,
  isEditForm,
  sourceLang: sourceLangProp,
  refundAction,
  hideTicketId = false,
  isInvitation = false,
}) => {
  const translate = useTranslate();
  const { localizedSource } = useSourceLanguage({ sourceLang: sourceLangProp });
  const priceField = useAdminPriceFieldProps(translate, isEditForm, scopedFormData);
  const { setValue, getValues } = useFormContext();

  React.useEffect(() => {
    if (!isInvitation) return;
    const forced = {
      price: 0,
      refundable: false,
      hasDownPayment: false,
      hasBasketLimit: false,
    };
    Object.entries(forced).forEach(([field, value]) => {
      if (getValues(getSource(field)) !== value) {
        setValue(getSource(field), value, { shouldDirty: true });
      }
    });
  }, [isInvitation, getSource, getValues, setValue]);

  const statusChoices = React.useMemo(
    () =>
      TICKET_GROUP_STATUSES.map((status) => ({
        ...status,
        name: translate(`form.status_${status.id}`, status.name),
      })),
    [translate],
  );

  return (
    <React.Fragment>
      <TicketIdInput getSource={getSource} hidden={hideTicketId} />

      <TextInput
        source={getSource(localizedSource('title'))}
        label={translate('form.title', 'Title')}
        validate={required()}
        fullWidth
        className="autoCapitalize"
        autoComplete="off"
      />

      <RichTextInput
        source={getSource(localizedSource('description'))}
        label={translate('form.description', 'Description')}
        validate={required()}
        fullWidth
        toolbar={<RichTextInputToolbar size="small" />}
      />

      <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <NumberInput
            onWheel={(event) => {
              event.target.blur();
            }}
            source={getSource('price')}
            type="number"
            label={translate('form.price', 'Price')}
            min={0}
            validate={required()}
            fullWidth
            defaultValue={isInvitation ? 0 : undefined}
            InputProps={isInvitation ? { readOnly: true } : priceField.InputProps}
            helperText={
              isInvitation
                ? translate('form.invitation_price_fixed', 'Davetiye ücretsizdir; fiyat 0.')
                : priceField.helperText
            }
          />
        </Box>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <NumberInput
            onWheel={(event) => {
              event.target.blur();
            }}
            source={getSource('stock')}
            type="number"
            label={translate('form.total_stock', 'Total Stock')}
            validate={required()}
            fullWidth
          />
        </Box>
      </Box>

      {true && (
        <NumberInput
          source={getSource('sold')}
          type="number"
          label={translate('form.total_sold', 'Total Sold (For this session)')}
          disabled
          fullWidth
        />
      )}

      {!isInvitation && (
        <React.Fragment>
          <ProductRefundFields getSource={getSource} refundAction={refundAction} />

          <ToggleRow
            getSource={getSource}
            toggleSource="hasDownPayment"
            label={translate('form.has_down_payment', 'Has Down Payment?')}
          >
            <NumberInput
              onWheel={(event) => {
                event.target.blur();
              }}
              source={getSource('downPaymentAmount')}
              type="number"
              label={translate('form.down_payment_price', 'Down Payment Price')}
              validate={required()}
              min={0}
              fullWidth
              size="small"
              margin="dense"
              helperText={false}
            />
          </ToggleRow>

          <ToggleRow
            getSource={getSource}
            toggleSource="hasBasketLimit"
            label={translate('form.has_basket_limit', 'Has Basket Limit?')}
          >
            <NumberInput
              onWheel={(event) => {
                event.target.blur();
              }}
              source={getSource('basketLimit')}
              type="number"
              label={translate('form.basket_limit', 'Basket Limit')}
              validate={required()}
              fullWidth
              size="small"
              margin="dense"
              helperText={false}
            />
          </ToggleRow>
        </React.Fragment>
      )}

      <RadioButtonGroupInput
        source={getSource('status')}
        label={translate('form.ticket_status', 'Status')}
        choices={statusChoices}
        defaultValue={0}
        validate={required()}
      />
    </React.Fragment>
  );
};

export const SeatedProductForm = ({
  getSource,
  showSold,
  scopedFormData,
  isEditForm,
  sourceLang: sourceLangProp,
  hideTicketId = false,
}) => {
  const translate = useTranslate();
  const { localizedSource } = useSourceLanguage({ sourceLang: sourceLangProp });
  const priceField = useAdminPriceFieldProps(translate, isEditForm, scopedFormData);

  return (
    <React.Fragment>
      <TicketIdInput getSource={getSource} hidden={hideTicketId} />

      <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <TextInput
            source={getSource(localizedSource('title'))}
            label={translate('form.title', 'Title')}
            validate={required()}
            fullWidth
            autoComplete="off"
          />
        </Box>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <TextInput
            source={getSource(localizedSource('description'))}
            label={translate('form.description', 'Description')}
            validate={required()}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </Box>

      <NumberInput
        onWheel={(event) => {
          event.target.blur();
        }}
        source={getSource('price')}
        type="number"
        label={translate('form.price', { _: 'Price' })}
        validate={required()}
        fullWidth
        InputProps={priceField.InputProps}
        helperText={priceField.helperText}
      />

      <ProductRefundFields getSource={getSource} />

      <BooleanInput
        source={getSource('isPromotionAvailable')}
        label={translate('form.promotion_available', { _: 'Promotion Available?' })}
        fullWidth
      />

      {scopedFormData && scopedFormData.isPromotionAvailable && (
        <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
          <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
            <TextInput
              source={getSource(localizedSource('promotion.label'))}
              label={translate('form.promotion_label', { _: 'Promotion Label' })}
              fullWidth
            />
          </Box>
          <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
            <NumberInput
              onWheel={(event) => {
                event.target.blur();
              }}
              source={getSource('promotion.price')}
              type="number"
              label={translate('form.promotion_price', { _: 'Promotion Price' })}
              fullWidth
              InputProps={priceField.InputProps}
              helperText={priceField.helperText}
            />
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};
